import { useEffect, useRef } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { styled } from '@mui/material/styles';
import { Box, Typography, FormHelperText } from '@mui/material';
import StarterKit from '@tiptap/starter-kit';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import Strike from '@tiptap/extension-strike';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import History from '@tiptap/extension-history';
import Heading from '@tiptap/extension-heading';
import {
    MenuButtonBold,
    MenuButtonItalic,
    MenuButtonUnderline,
    MenuButtonStrikethrough,
    MenuButtonBulletedList,
    MenuButtonOrderedList,
    MenuButtonTextColor,
    MenuButtonHighlightColor,
    MenuButtonEditLink,
    MenuButtonHorizontalRule,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    MenuSelectFontFamily,
    MenuSelectTextAlign,
    MenuButtonUndo,
    MenuButtonRedo,
    RichTextEditor,
    LinkBubbleMenu,
    LinkBubbleMenuHandler,
} from 'mui-tiptap';
import { theme } from 'context/theme';

const StyledRichEditorContainer = styled(Box)(({ theme, error }) => ({
    border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(['border-color'], {
        duration: theme.transitions.duration.short,
    }),
    overflow: 'hidden',

    '&:hover': {
        borderColor: error ? theme.palette.error.main : theme.palette.text.primary,
    },

    '&:focus-within': {
        borderColor: error
            ? theme.palette.error.main
            : `${theme.palette.secondary.main} !important`,
    },

    // Target any nested elements that might have focus borders
    '& .MuiTiptap-RichTextEditor-root *': {
        '&:focus-within, &:focus, &.Mui-focused': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },
    },

    // Override any fieldset or outline styles
    '& fieldset': {
        borderColor: `${theme.palette.divider} !important`,
        '&:focus-within, &:focus, &.Mui-focused': {
            borderColor: `${theme.palette.secondary.main} !important`,
            borderWidth: '2px !important',
        },
    },

    // Override any Material-UI specific focus classes
    '&.Mui-focused, &:has(.Mui-focused)': {
        borderColor: `${theme.palette.secondary.main} !important`,
    },

    '& .MuiTiptap-RichTextEditor-root': {
        border: 'none',
        borderRadius: 'inherit',
        backgroundColor: 'transparent',
        '&:focus-within, &:focus, &.Mui-focused': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },
    },

    '& .MuiTiptap-RichTextField-root .MuiTiptap-RichTextField-menuBarContent': {
        padding: 0,
    },

    '& .MuiTiptap-MenuControlsContainer-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.grey[50],
        padding: theme.spacing(1),
    },

    '& .MuiTiptap-RichTextContent-root': {
        height: '360px',
        padding: theme.spacing(2),
        backgroundColor: 'hsl(0 0% 10%)', // Should be defined in theme instead
        overflow: 'auto',
        '&:focus-within, &:focus, &.Mui-focused': {
            borderColor: `${theme.palette.secondary.main} !important`,
        },

        '& .ProseMirror': {
            outline: 'none',
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.body1.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
            color: 'hsl(0 0% 95%)', // Should be defined in theme instead
            minHeight: '100%',

            '&.ProseMirror-focused': {
                outline: 'none',
            },

            '& p.is-editor-empty:first-child::before': {
                content: 'attr(data-placeholder)',
                float: 'left',
                color: theme.palette.text.secondary,
                pointerEvents: 'none',
                height: 0,
            },

            // Ensure headings maintain proper styling and don't affect other elements
            '& .heading': {
                display: 'block',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(1),
                '&:first-child': {
                    marginTop: 0,
                },
                '&:last-child': {
                    marginBottom: 0,
                },
            },

            '& hr': {
                backgroundColor: 'hsl(0 0% 95%)',
            },

            '& h1.heading': {
                fontSize: '2rem',
                fontWeight: 700,
                lineHeight: 1.2,
            },

            '& h2.heading': {
                fontSize: '1.75rem',
                fontWeight: 600,
                lineHeight: 1.3,
            },

            '& h3.heading': {
                fontSize: '1.5rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },

            '& h4.heading': {
                fontSize: '1.25rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },

            '& h5.heading': {
                fontSize: '1.1rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },

            '& h6.heading': {
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.4,
            },
        },
    },
}));

const customTheme = createTheme({
    ...theme,
    palette: {
        ...theme.palette,
        primary: {
            main: theme.palette.secondary.main,
        },
    },
});

const disabledColorPicker = {
    sx: {
        // https://github.com/omgovich/react-colorful/blob/master/src/css/styles.css#L1
        '& .react-colorful': {
            display: 'none !important',
        },
        '& .MuiTextField-root': {
            display: 'none !important',
        },
        '& .MuiInputBase-root': {
            display: 'none !important',
        },
        '& .MuiOutlinedInput-root': {
            display: 'none !important',
        },
    },
};

const SWATCH_COLORS = ['#4ade80', '#60a5fa', '#f87171', '#a78bfa', '#facc15'];

export const RichEditor = ({
    label,
    value,
    onChange,
    placeholder = 'Enter text...',
    error = false,
    helperText = '',
    ...props
}) => {
    const rteRef = useRef(null);
    const prevValue = useRef(value);

    const handleChange = (editor) => {
        onChange({
            text: editor.getText(),
            html: editor.getHTML(),
        });
    };

    // Update editor content when value prop changes externally (like from cancel/reset)
    useEffect(() => {
        if (value !== prevValue.current && rteRef.current && rteRef.current.editor) {
            const currentContent = rteRef.current.editor.getHTML();
            if (currentContent !== value) {
                rteRef.current.editor.commands.setContent(value || '');
            }
            prevValue.current = value;
        }
    }, [value]);

    return (
        <Box sx={{ mb: 3, mt: 3 }}>
            {label && (
                <Typography
                    variant="body2"
                    color={error ? 'error' : 'text.secondary'}
                    sx={{ mb: 1 }}
                >
                    {label}
                </Typography>
            )}
            <StyledRichEditorContainer error={error}>
                <ThemeProvider theme={customTheme}>
                    <RichTextEditor
                        ref={rteRef}
                        extensions={[
                            StarterKit.configure({
                                // Disable these extensions as we're using individual ones
                                bulletList: false,
                                orderedList: false,
                                listItem: false,
                                strike: false,
                                history: false,
                                heading: false, // Disable default heading to use custom one
                            }),
                            Heading.configure({
                                levels: [1, 2, 3, 4, 5, 6],
                                HTMLAttributes: {
                                    class: 'heading',
                                },
                            }),
                            TextStyle,
                            Color,
                            FontFamily,
                            Highlight.configure({ multicolor: true }),
                            HorizontalRule,
                            Link.configure({
                                openOnClick: false,
                                HTMLAttributes: {
                                    class: 'link',
                                },
                            }),
                            LinkBubbleMenuHandler,
                            ListItem,
                            BulletList.configure({
                                HTMLAttributes: {
                                    class: 'bullet-list',
                                },
                            }),
                            OrderedList.configure({
                                HTMLAttributes: {
                                    class: 'ordered-list',
                                },
                            }),
                            Strike,
                            TextAlign.configure({
                                types: ['heading', 'paragraph'],
                            }),
                            Underline,
                            History,
                        ]}
                        content={value}
                        onUpdate={({ editor }) => {
                            handleChange(editor);
                        }}
                        editorProps={{
                            attributes: {
                                'data-placeholder': placeholder,
                            },
                        }}
                        renderControls={() => (
                            <MenuControlsContainer>
                                <MenuButtonUndo />
                                <MenuButtonRedo />
                                <MenuDivider />
                                <MenuSelectHeading />
                                <MenuSelectFontFamily
                                    options={[
                                        { label: 'Monospace', value: 'monospace' },
                                        { label: 'Serif', value: 'serif' },
                                        { label: 'Arial', value: 'arial' },
                                        { label: 'Times New Roman', value: 'times-new-roman' },
                                        { label: 'Courier New', value: 'courier-new' },
                                        { label: 'Roboto', value: 'roboto' },
                                        { label: 'Open Sans', value: 'open-sans' },
                                        { label: 'Montserrat', value: 'montserrat' },
                                    ]}
                                    aria-label="Font families"
                                    emptyLabel="Font family"
                                    tooltipTitle="Change font family"
                                    unsetOptionLabel="Reset"
                                />
                                <MenuDivider />
                                <MenuButtonBold />
                                <MenuButtonItalic />
                                <MenuButtonUnderline />
                                <MenuButtonStrikethrough />
                                <MenuDivider />
                                <MenuButtonTextColor
                                    swatchColors={SWATCH_COLORS}
                                    PopperProps={disabledColorPicker}
                                />
                                <MenuButtonHighlightColor
                                    swatchColors={SWATCH_COLORS}
                                    PopperProps={disabledColorPicker}
                                />
                                <MenuDivider />
                                <MenuSelectTextAlign />
                                <MenuDivider />
                                <MenuButtonBulletedList />
                                <MenuButtonOrderedList />
                                <MenuDivider />
                                <MenuButtonEditLink />
                                <MenuButtonHorizontalRule />
                            </MenuControlsContainer>
                        )}
                        {...props}
                    >
                        {() => (
                            <>
                                <LinkBubbleMenu />
                            </>
                        )}
                    </RichTextEditor>
                </ThemeProvider>
            </StyledRichEditorContainer>
            {helperText ? (
                <FormHelperText error={error} sx={{ ml: 1.75, mt: 0.5 }}>
                    {helperText}
                </FormHelperText>
            ) : (
                <FormHelperText sx={{ ml: 1.75, mt: 0.5 }}>
                    Description is shown on a dark background on the storefront
                </FormHelperText>
            )}
        </Box>
    );
};
